// ---------------------------------------------------------------------------
// Derives the map/canvas's 5 mutually-adjacent "modes" from the loaded
// project's own UI state — split out of App.tsx (see
// project_evalhid_code_audit.md's ongoing "god module" breakup). Pure
// derivation, no hooks of its own (React's hook-ordering rules don't apply —
// it's a plain function, called the same way `state`/`activePanel`/
// `zoneAssignMode` themselves already are, after App.tsx's own early
// returns for the non-"loaded" phases).
// ---------------------------------------------------------------------------
import type { AssignmentColorMode } from "../model/assignmentColors.ts";
import type { MapMitecoMode } from "../model/mitecoLayers.ts";
import type { ResultLevel, ResultSelection } from "./resultSelection.ts";
import type {
  MapResultsMode,
  MapSelectionMode,
  MapSeriesMode,
  SeriesSelection,
} from "./mapModes.ts";
import type { State } from "./appState.ts";
import type { PanelKind } from "./editor/panelDefinitions.ts";

type LoadedState = Extract<State, { phase: "loaded" }>;

interface MapModes {
  selectionMode: MapSelectionMode | undefined;
  resultsMode: MapResultsMode | undefined;
  seriesMode: MapSeriesMode | undefined;
  mitecoMode: MapMitecoMode | undefined;
  colorMode: AssignmentColorMode | null;
}

export function deriveMapModes(
  state: LoadedState,
  activePanel: PanelKind,
  zoneAssignMode: "zone" | "snow",
  resultsLevel: ResultLevel,
  callbacks: {
    changeMapSelection: (selection: Set<number>) => void;
    selectResult: (selection: ResultSelection) => void;
    selectSeries: (selection: SeriesSelection) => void;
    changeMitecoSelected: (selected: Map<string, GeoJSON.Feature>) => void;
  }
): MapModes {
  // The "Input series" tab has its own click behavior on the map/canvas
  // (filtering its charts, see seriesMode below) instead of the usual bulk
  // (de)selection — mutually exclusive with it, same as selectionMode/
  // resultsMode already are with each other. Also excluded while
  // mitecoMode is explicitly on (SubBasinsPanel.tsx's "Add from MITECO"
  // button) even on the "subBasins" tab where both would otherwise apply
  // — both bind their own rectangle-select on the map (RectangleSelectionLayer)
  // and would fight over the same drag gesture.
  const selectionMode: MapSelectionMode | undefined =
    state.editorPanelOpen && activePanel !== "series" && !state.mitecoMode
      ? { selection: state.mapSelection, onCambiarSeleccion: callbacks.changeMapSelection }
      : undefined;
  // Only while the editor panel is closed: if both were open at once, a
  // click on the map would serve to edit (assign zone, delete...), not to choose which result to view.
  const resultsMode: MapResultsMode | undefined =
    state.resultsPanelOpen && !state.editorPanelOpen
      ? { selection: state.resultSelection, onSelect: callbacks.selectResult }
      : undefined;
  const seriesMode: MapSeriesMode | undefined =
    state.editorPanelOpen && activePanel === "series"
      ? { selection: state.seriesSelection, onSelect: callbacks.selectSeries }
      : undefined;
  // Only on the "subBasins" tab — see SubBasinsPanel.tsx's own "Add from
  // MITECO" button, the only way state.mitecoMode becomes non-null.
  const activeMitecoMode = state.mitecoMode;
  const mitecoMode: MapMitecoMode | undefined =
    state.editorPanelOpen && activePanel === "subBasins" && activeMitecoMode
      ? {
          layer: activeMitecoMode.layer,
          selected: activeMitecoMode.selected,
          onChangeSelected: callbacks.changeMitecoSelected,
          alreadyAdded: new Set(
            Object.values(state.mitecoOrigin)
              .filter((o) => o.layer === activeMitecoMode.layer)
              .map((o) => o.basinCode)
          ),
        }
      : undefined;
  // Color sub-basins on the map by whichever entity they're assigned to
  // (assignmentColors.ts) — either while EditBar.tsx is bulk-editing one of
  // 3 catalogs, or while the Results panel is showing the "Outlet point"/
  // "Zone" level (resultsLevel, from CalculationResults.tsx's own tab via
  // usePanels.ts) — seeing which sub-basins feed which outlet/zone is the
  // same "assigned to" question either way, just read from the results tab
  // instead of the editor's. The editor takes priority if both panels
  // happen to be open (unlikely — resultsMode's own click handling already
  // defers to editing in that case, see above), and results coloring never
  // applies to the "Sub-basin"/"Aquifer" levels (no useful grouping there:
  // every sub-basin is its own entity, an aquifer has no "assigned"
  // sub-basins field of its own the way outlet/zone do).
  // SubBasinCanvas.tsx computes its own colorMode from its own tab bar
  // instead of reading this one — see that component's comment on why.
  const colorModeProject = state.loadedProject.project;
  const colorMode: AssignmentColorMode | null = state.editorPanelOpen
    ? activePanel === "zones"
      ? {
          field: zoneAssignMode === "zone" ? "zoneCode" : "snowZoneCode",
          entities: (zoneAssignMode === "zone"
            ? colorModeProject.zones.filter((z) => z.modelTypeCode != null)
            : colorModeProject.zones.filter((z) => z.snowModelTypeCode != null)
          ).map((z) => ({ code: z.zoneCode, name: z.name })),
        }
      : activePanel === "outlets"
        ? {
            field: "outletCode",
            entities: colorModeProject.outletPoints.map((p) => ({
              code: p.outletCode,
              name: p.name,
            })),
          }
        : activePanel === "aquifers"
          ? {
              field: "aquiferCode",
              entities: colorModeProject.aquifers.map((a) => ({
                code: a.aquiferCode,
                name: a.name,
              })),
            }
          : null
    : state.resultsPanelOpen && resultsLevel === "outletPoint"
      ? {
          field: "outletCode",
          entities: colorModeProject.outletPoints.map((p) => ({
            code: p.outletCode,
            name: p.name,
          })),
        }
      : state.resultsPanelOpen && resultsLevel === "zone"
        ? {
            field: "zoneCode",
            entities: colorModeProject.zones
              .filter((z) => z.modelTypeCode != null)
              .map((z) => ({ code: z.zoneCode, name: z.name })),
          }
        : null;

  return { selectionMode, resultsMode, seriesMode, mitecoMode, colorMode };
}
